import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, Phone, Eye, EyeOff, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: prefillEmail,
    phone: '',
    password: '',
    confirm_password: '',
    role: 'client'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/client/register`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: formData.role
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/client/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="page-container client-auth-page" data-testid="register-success-page">
        <div className="auth-card success-card">
          <img 
            src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
            alt="JNT" 
            className="auth-logo"
          />
          <CheckCircle size={56} className="success-icon" />
          <h2>Welcome to Journeys N Tales!</h2>
          <p style={{ color: '#718096', marginBottom: '0.5rem' }}>Your account has been created successfully.</p>
          <p style={{ color: '#718096', fontSize: '0.9rem' }}>A confirmation email has been sent to <strong>{formData.email}</strong></p>
          <p style={{ color: '#b8976c', fontSize: '0.85rem', marginTop: '1rem' }}>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container client-auth-page" data-testid="client-register-page">
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
            alt="JNT" 
            className="auth-logo"
          />
          <h1>Create Your Account</h1>
          <p>Join Journeys N Tales to access your travel plans</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-banner" data-testid="register-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Role Selector */}
          <div className="form-group">
            <label>I am a</label>
            <div className="input-with-icon">
              <User size={18} />
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="role-select"
                data-testid="role-select"
              >
                <option value="client">Client</option>
                <option value="partner">Partner</option>
              </select>
              <ChevronDown size={18} className="select-arrow" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <div className="input-with-icon">
                <User size={18} />
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="First name"
                  required
                  data-testid="first-name-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <div className="input-with-icon">
                <User size={18} />
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Last name"
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Email</label>
            <div className="input-with-icon">
              <Mail size={18} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Phone</label>
            <div className="input-with-icon">
              <Phone size={18} />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Password</label>
            <div className="input-with-icon">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Min 8 characters"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                data-testid="toggle-password"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <div className="input-with-icon">
              <Lock size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder="Re-enter password"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                data-testid="toggle-confirm-password"
              >
                {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>

          <Button type="submit" className="submit-btn" disabled={loading} data-testid="register-submit">
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <a href="/client/login">Sign In</a></p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegister;
